import type { BannerTexts, BannerTheme, ConsentState } from './types';

/**
 * Default inline style values used when theme is not provided.
 * These act as a last-resort defense: even if the <style> tag is stripped
 * by a content blocker (e.g. DIA browser's privacy shield), the banner
 * elements will still have visible backgrounds and colors.
 */
const INLINE_DEFAULTS = {
  primaryColor: '#06b6d4',
  secondaryColor: '#f3f4f6',
  textColor: '#1f2937',
  backgroundColor: '#ffffff',
  borderRadius: 8,
  fontFamily: 'system-ui, -apple-system, sans-serif',
  fontSize: 14,
};

/**
 * Get resolved inline style values from theme or defaults
 */
function getInlineColors(theme?: BannerTheme) {
  return {
    primary: theme?.primaryColor || INLINE_DEFAULTS.primaryColor,
    secondary: theme?.secondaryColor || INLINE_DEFAULTS.secondaryColor,
    text: theme?.textColor || INLINE_DEFAULTS.textColor,
    bg: theme?.backgroundColor || INLINE_DEFAULTS.backgroundColor,
    radius: theme?.borderRadius ?? INLINE_DEFAULTS.borderRadius,
    font: theme?.fontFamily || INLINE_DEFAULTS.fontFamily,
    fontSize: theme?.fontSize ?? INLINE_DEFAULTS.fontSize,
  };
}

/**
 * Create the main banner HTML
 * Inline styles are applied to all critical elements as a hardening measure
 * against content blockers (like DIA browser) that may strip the <style> tag.
 */
export function createBannerHTML(
  texts: BannerTexts,
  theme?: BannerTheme,
  showRejectButton: boolean = true,
  showPreferencesButton: boolean = true,
  showBranding: boolean = true
): string {
  const c = getInlineColors(theme);

  const rejectButton = showRejectButton
    ? `<button id="cookied-reject-all" class="cookied-btn cookied-btn-secondary" type="button" style="background:${c.secondary};color:${c.text};padding:10px 18px;border:none;border-radius:${c.radius - 2}px;font-family:${c.font};font-size:${c.fontSize}px;font-weight:500;cursor:pointer">${escapeHtml(texts.rejectAll)}</button>`
    : '';

  const preferencesButton = showPreferencesButton
    ? `<button id="cookied-preferences" class="cookied-btn cookied-btn-link" type="button" style="background:transparent;color:${c.text};text-decoration:underline;padding:10px 12px;border:none;font-family:${c.font};font-size:${c.fontSize}px;font-weight:500;cursor:pointer">${escapeHtml(texts.managePreferences)}</button>`
    : '';

  const branding = showBranding
    ? `<div class="cookied-branding" style="margin-top:10px;text-align:center;font-size:11px;opacity:0.6">Powered by <a href="https://cookied.io?ref=banner" target="_blank" rel="noopener" title="Cookied - Cookie Consent Solution for GDPR, CCPA &amp; LGPD Compliance" style="color:inherit;text-decoration:none">Cookied</a></div>`
    : '';

  return `
    <div class="cookied-container" style="background:${c.bg};border-radius:${c.radius}px;box-shadow:0 4px 20px rgba(0,0,0,0.15);padding:16px 20px">
      <div class="cookied-content" style="display:flex;align-items:center;gap:16px">
        <div class="cookied-text" style="flex:1">
          <h2 class="cookied-title" style="font-weight:600;font-size:${c.fontSize + 2}px;margin:0 0 6px 0;color:${c.text}">${escapeHtml(texts.title)}</h2>
          <p class="cookied-description" style="margin:0;line-height:1.5;color:${c.text};opacity:0.85">${escapeHtml(texts.description)}</p>
        </div>
        <div class="cookied-buttons" style="display:flex;gap:8px;flex-wrap:wrap">
          <button id="cookied-accept-all" class="cookied-btn cookied-btn-primary" type="button" style="background:${c.primary};color:white;padding:10px 18px;border:none;border-radius:${c.radius - 2}px;font-family:${c.font};font-size:${c.fontSize}px;font-weight:500;cursor:pointer">${escapeHtml(texts.acceptAll)}</button>
          ${rejectButton}
          ${preferencesButton}
        </div>
      </div>
      ${branding}
    </div>
  `;
}

/**
 * Create the preferences modal HTML
 * Same inline style hardening as the banner.
 */
export function createPreferencesHTML(
  texts: BannerTexts,
  theme?: BannerTheme,
  currentConsent: ConsentState = { necessary: true, analytics: false, marketing: false },
  allowPreChecked: boolean = false
): string {
  const c = getInlineColors(theme);

  const categories = [
    {
      id: 'necessary',
      ...texts.categories.necessary,
      checked: true,
      disabled: true,
    },
    {
      id: 'analytics',
      ...texts.categories.analytics,
      checked: allowPreChecked ? currentConsent.analytics : currentConsent.analytics,
      disabled: false,
    },
    {
      id: 'marketing',
      ...texts.categories.marketing,
      checked: allowPreChecked ? currentConsent.marketing : currentConsent.marketing,
      disabled: false,
    },
  ];

  const categoryHTML = categories
    .map(
      (cat) => `
        <div class="cookied-category" style="padding:16px;background:${c.secondary};border-radius:${c.radius - 2}px">
          <div class="cookied-category-header" style="display:flex;justify-content:space-between;align-items:center;margin-bottom:6px">
            <span class="cookied-category-title" style="font-weight:600;font-size:${c.fontSize + 1}px">${escapeHtml(cat.title)}</span>
            <label class="cookied-toggle">
              <input 
                type="checkbox" 
                id="cookied-${cat.id}" 
                ${cat.checked ? 'checked' : ''} 
                ${cat.disabled ? 'disabled' : ''}
              >
              <span class="cookied-toggle-slider"></span>
            </label>
          </div>
          <p class="cookied-category-description" style="font-size:${c.fontSize - 1}px;opacity:0.8;line-height:1.4;margin:0">${escapeHtml(cat.description)}</p>
        </div>
      `
    )
    .join('');

  return `
    <div class="cookied-modal" role="dialog" aria-labelledby="cookied-modal-title" aria-modal="true" style="background:${c.bg};border-radius:${c.radius}px;box-shadow:0 10px 40px rgba(0,0,0,0.2);padding:24px;max-width:500px;width:90%;max-height:80vh;overflow-y:auto">
      <div class="cookied-modal-header" style="display:flex;justify-content:space-between;align-items:center;margin-bottom:16px">
        <h3 id="cookied-modal-title" class="cookied-modal-title" style="font-size:18px;font-weight:600;margin:0;color:${c.text}">${escapeHtml(texts.managePreferences)}</h3>
        <button id="cookied-close-preferences" class="cookied-modal-close" type="button" aria-label="Close" style="background:none;border:none;font-size:24px;cursor:pointer;padding:4px;line-height:1;opacity:0.6">&times;</button>
      </div>
      <div class="cookied-categories" style="display:flex;flex-direction:column;gap:16px;margin-bottom:20px">
        ${categoryHTML}
      </div>
      <div class="cookied-modal-footer" style="display:flex;justify-content:flex-end;gap:8px">
        <button id="cookied-save-preferences" class="cookied-btn cookied-btn-primary" type="button" style="background:${c.primary};color:white;padding:10px 18px;border:none;border-radius:${c.radius - 2}px;font-family:${c.font};font-size:${c.fontSize}px;font-weight:500;cursor:pointer">${escapeHtml(texts.savePreferences)}</button>
      </div>
    </div>
  `;
}

/**
 * Escape HTML special characters
 */
function escapeHtml(str: string): string {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}
